/* eslint-disable no-console */
// Vinculación de un movimiento bancario a 1..N facturas SII, cada factura saldada por sus CUOTAS
// (una o varias) o por un MONTO manual (facturas sin plan). Fuente ÚNICA de escritura del flujo
// "Pago/Cobro SII" del modal (10.1 / 10.103): reemplaza los dos caminos históricos —single con una
// cuota (handleConfirmar) y múltiple sin cuotas (handleConfirmarMultiple)— por una sola lógica que
// no depende de cuántas facturas se eligen.
//
// Modelo de datos (SIN migración de esquema — decisión tomada):
//   - El vínculo AUTORITATIVO cuota↔movimiento es `plan_pagos_facturas.movimiento_bancario_id`
//     (N→1: varias cuotas pueden colgar del mismo movimiento). Lo escribe este util por cada cuota.
//   - `movimientos_bancarios.plan_pago_cuota_id` / `plan_cobro_cuota_id` son ESCALARES y no pueden
//     representar N cuotas: este util NO los escribe nunca (los deja en null). Los motores de cobro
//     (getCobroFactura Camino 1 MBF / getFacturasYaVinculadas Fuente 3) resuelven la cobranza por
//     Σ `movimiento_bancario_facturas.monto`, que este util deja correcto (una fila por factura con
//     el monto APLICADO a esa factura, no el débito entero — fix del bug del puente).
//   - FK escalar de factura (`factura_sii_recibida_id`/`emitida_id`): B3 Fase B — ya NO se escribe (ni
//     single ni multi). La vinculación vive SIEMPRE en la tabla puente MBF; el estado de cobro lo
//     resuelve `getCobroReal` por el puente. (`fkEscalar` en el return sigue indicando "fue single";
//     el caller lo usa solo para el embed OPTIMISTA del badge, no para escribir la FK.)
//
// El cuadre TOTAL (Σ montos aplicados vs |débito|) reusa `evaluarCuadreCuotas` (10.103). Multi-moneda
// por la FECHA del movimiento vía `convertirAMonedaLocal` (10.185). Sin fallo silencioso (10.177):
// cada escritura chequea su `{ error }`. `supabaseClient` se inyecta (default al singleton) para test.

import { evaluarCuadreCuotas } from "./cuadreCuotas";
import { TOLERANCIA_REDONDEO_LOCAL } from "./estadoCuenta";
import { convertirAMonedaLocal } from "./exchangeRateService";
import { client } from "./supabase";

const monedaLocalDe = (pais) => (pais === "Perú" ? "PEN" : "CLP");

// Cuotas tildadas de una factura seleccionada (shape del modal). [] si no hay plan / no se tildó nada.
const cuotasDe = (fSel) => (Array.isArray(fSel?.cuotasSeleccionadas) ? fSel.cuotasSeleccionadas : []);

// Monto aplicado a UNA factura (en la moneda de la factura):
//   - con cuotas tildadas → Σ monto_cuota de las cuotas tildadas (derivado, NO editable).
//   - sin plan → montoAsignado (manual). La UI SIEMPRE lo pre-carga para facturas sin plan.
//   - con plan pero SIN cuotas tildadas → 0 (NUNCA monto_total): así el cuadre no pasa por defecto
//     y no se puede confirmar una factura con plan sin elegir cuotas (evita cuotas huérfanas).
// Es el monto que va a la fila MBF de esa factura (fix del puente: antes iba el débito entero).
export const montoAplicadoFactura = (fSel) => {
  const cuotas = cuotasDe(fSel);
  if (cuotas.length > 0) return cuotas.reduce((s, c) => s + Math.abs(Number(c.monto_cuota || 0)), 0);
  return Number(fSel?.montoAsignado != null ? fSel.montoAsignado : 0);
};

// Moneda de una factura para comparar/convertir: la recibida (pago) siempre es local; la emitida
// (cobro) hereda su propia `moneda` (puede ser USD en DTE 110).
const monedaFacturaDe = (fSel, esCobro, pais) => (esCobro ? fSel?.moneda || monedaLocalDe(pais) : monedaLocalDe(pais));

// Cuadre TOTAL del movimiento contra la Σ de montos aplicados (una entrada por factura), en moneda
// local por la FECHA del movimiento (10.185). Reusa evaluarCuadreCuotas pasando las facturas como
// "conjunto de cuotas" con montoDe = monto aplicado convertido. Tolerancia = redondeo (10) escalado
// por la cantidad de cuotas tildadas (mínimo 1 por factura, como el single).
//   - asignado:   Σ montos aplicados (local).
//   - montoMovCmp: |débito| en local.
//   - sinAsignar: montoMovCmp − asignado, CON signo (>0 falta asignar / <0 sobre-asignado).
//   - cuadra:     |sinAsignar| ≤ tolerancia.
export const evaluarCuadreAplicaciones = ({ facturasSeleccionadas = [], movimiento, esCobro, pais }) => {
  const local = monedaLocalDe(pais);
  const monedaMov = movimiento?.moneda || local;
  const montoMovCmp =
    monedaMov !== local
      ? convertirAMonedaLocal(Math.abs(Number(movimiento?.monto || 0)), monedaMov, pais, movimiento?.fecha)
      : Math.abs(Number(movimiento?.monto || 0));

  const montoDe = (fSel) => {
    const aplicado = montoAplicadoFactura(fSel);
    const monedaFac = monedaFacturaDe(fSel, esCobro, pais);
    return monedaFac !== local ? convertirAMonedaLocal(aplicado, monedaFac, pais, movimiento?.fecha) : aplicado;
  };

  const nCuotas = facturasSeleccionadas.reduce((n, f) => n + Math.max(cuotasDe(f).length, 1), 0);
  const tolerancia = Math.max(nCuotas, 1) * TOLERANCIA_REDONDEO_LOCAL;

  const {
    sumaCuotas: asignado,
    diferencia,
    cuadra,
  } = evaluarCuadreCuotas(facturasSeleccionadas, montoMovCmp, tolerancia, montoDe);
  return { asignado, montoMovCmp, sinAsignar: diferencia, cuadra, moneda: local, tolerancia };
};

// Neto / impuesto de la caja consolidada, prorrateados por el monto aplicado de cada factura
// (montoAplicado / monto_total). Con cuotas parciales el neto no se sobre-declara; con factura
// completa el ratio es 1 y coincide con el total. En moneda cruda de cada factura (la caja hereda
// la moneda del movimiento; el desglose neto/impuesto es informativo).
const netoImpuestoConsolidado = (facturasSeleccionadas) => {
  let neto = 0;
  let impuesto = 0;
  for (const f of facturasSeleccionadas) {
    const total = Number(f.monto_total || 0);
    const ratio = total > 0 ? montoAplicadoFactura(f) / total : 1;
    neto += Number(f.monto_neto || 0) * ratio;
    impuesto += Number(f.monto_iva || 0) * ratio;
  }
  return { neto: Math.round(neto), impuesto: Math.round(impuesto) };
};

// ── FASE 4 (dual-write) — espejo de la vinculación al libro `aplicaciones_saldo` ─────────────────
// Escribe un evento `via='banco'` por CADA aplicación (una cuota tildada, o UNA aplicación manual si
// la factura no tiene plan), reflejando lo mismo que las filas MBF/cuota del write autoritativo. NO
// reemplaza nada (Fase 5); es un espejo adicional que resolverSaldo(documento) suma. Solo SII.

// Congela la conversión de UNA aplicación (10.185): monto_documento en la moneda del documento;
// monto_origen en la moneda en que se movió la plata (la del movimiento).
const congelarConversionEvento = ({ montoDoc, monedaFac, monedaMov, local, pais, fecha }) => {
  if (monedaMov === monedaFac)
    return { monto_origen: montoDoc, moneda_origen: monedaFac, tc: null, fecha_tc: null, warn: null };
  if (monedaMov === local) {
    // Documento en moneda extranjera (ej. emitida USD) y pago en moneda local: convertir el monto del
    // documento a local y congelar el TC de la fecha del movimiento.
    const enLocal = convertirAMonedaLocal(montoDoc, monedaFac, pais, fecha);
    return {
      monto_origen: enLocal,
      moneda_origen: local,
      tc: montoDoc ? enLocal / montoDoc : null,
      fecha_tc: fecha,
      warn: null,
    };
  }
  // Caso raro (pago en una moneda extranjera distinta a la del documento): el conversor solo va a
  // local → se degrada a la moneda del documento (resolverSaldo sigue correcto por monto_documento)
  // y se marca para que NO pase en silencio (10.177/10.170).
  return {
    monto_origen: montoDoc,
    moneda_origen: monedaFac,
    tc: null,
    fecha_tc: null,
    warn: `cross-currency no soportado por el conversor (mov ${monedaMov} / doc ${monedaFac}); evento congelado en la moneda del documento`,
  };
};

// Construye (puro, testeable) los eventos de `aplicaciones_saldo` de una vinculación: un evento por
// cuota tildada (cuota_id + monto_cuota) o UNO por factura sin plan (cuota_id null + montoAsignado).
// eje pago=recibida / cobro=emitida; via='banco'; origen = el movimiento. Σ eventos por documento =
// monto aplicado (= la fila MBF) → resolverSaldo da el mismo saldo que la lógica vieja.
// `documentoTipo` (opcional) fuerza el tipo del documento (F1-B1: 'boleta_honorario'); sin él se
// deriva de esCobro (sii_emitida / sii_recibida). El `eje` se deriva SIEMPRE del tipo — igual que el
// CHECK `chk_aplicaciones_saldo_eje_tipo` (emitida→cobro, resto→pago). Para boletas, la moneda del
// documento es local (CLP): `monedaFacturaDe(f, esCobro=false, pais)` ya la da correcta.
export const construirEventosSaldo = ({
  movimiento,
  facturasSeleccionadas = [],
  esCobro,
  pais,
  usuarioId = null,
  documentoTipo = null,
}) => {
  const local = monedaLocalDe(pais);
  const monedaMov = movimiento?.moneda || local;
  const documento_tipo = documentoTipo || (esCobro ? "sii_emitida" : "sii_recibida");
  const eje = documento_tipo === "sii_emitida" ? "cobro" : "pago";
  const eventos = [];
  const warnings = [];
  for (const f of facturasSeleccionadas) {
    const monedaFac = monedaFacturaDe(f, esCobro, pais);
    const cuotas = cuotasDe(f);
    const aplicaciones =
      cuotas.length > 0
        ? cuotas.map((c) => ({ cuota_id: c.id, montoDoc: Math.abs(Number(c.monto_cuota || 0)) }))
        : [{ cuota_id: null, montoDoc: Number(f.montoAsignado != null ? f.montoAsignado : 0) }];
    for (const a of aplicaciones) {
      if (!(a.montoDoc > 0)) continue; // no sembrar aplicaciones de monto 0
      const conv = congelarConversionEvento({
        montoDoc: a.montoDoc,
        monedaFac,
        monedaMov,
        local,
        pais,
        fecha: movimiento.fecha,
      });
      if (conv.warn) warnings.push(`factura ${f.id}: ${conv.warn}`);
      eventos.push({
        documento_tipo,
        documento_id: f.id,
        eje,
        via: "banco",
        origen_tipo: "movimiento_bancario",
        origen_id: movimiento.id,
        cuota_id: a.cuota_id,
        monto_origen: conv.monto_origen,
        moneda_origen: conv.moneda_origen,
        tc: conv.tc,
        fecha_tc: conv.fecha_tc,
        monto_documento: a.montoDoc,
        fecha_evento: movimiento.fecha,
        monto: conv.monto_origen, // banco: positivo (paga)
        created_by: usuarioId || null,
      });
    }
  }
  return { eventos, warnings };
};

// Eventos de una vinculación de facturas EXTERNAS (Fase 4). Solo pago COMPLETO (el handler bloquea
// parciales): un evento via='banco' por externa, monto_documento = monto_total (moneda del documento);
// moneda_origen = moneda del movimiento (el banco paga en local); monto_origen = la porción del débito
// prorrateada por monto_total (MISMA fórmula que la fila MBF del handler). Congela tc si el documento
// no está en la moneda del movimiento (externa USD pagada con banco CLP — 10.185).
export const construirEventosExterna = ({ movimiento, externasSeleccionadas = [], pais, usuarioId = null }) => {
  const local = monedaLocalDe(pais);
  const monedaMov = movimiento?.moneda || local;
  const montoBanco = Math.abs(Number(movimiento?.monto || 0));
  const totalFacturas = externasSeleccionadas.reduce((s, f) => s + Number(f?.monto_total || 0), 0);
  return externasSeleccionadas
    .filter((f) => f && f.id != null && Number(f.monto_total) > 0)
    .map((f) => {
      const montoTotal = Number(f.monto_total);
      const mbfMonto = totalFacturas > 0 ? montoBanco * (montoTotal / totalFacturas) : montoBanco;
      const monedaFac = f.moneda || local;
      const cross = monedaMov !== monedaFac;
      return {
        documento_tipo: "factura_externa",
        documento_id: f.id,
        eje: "pago",
        via: "banco",
        origen_tipo: "movimiento_bancario",
        origen_id: movimiento.id,
        cuota_id: null,
        monto_origen: mbfMonto,
        moneda_origen: monedaMov,
        tc: cross && montoTotal ? mbfMonto / montoTotal : null,
        fecha_tc: cross ? movimiento.fecha : null,
        monto_documento: montoTotal,
        fecha_evento: movimiento.fecha,
        monto: mbfMonto,
        created_by: usuarioId || null,
      };
    });
};

// Escribe los eventos al libro IDEMPOTENTE (borra los del MISMO origen y re-inserta) y BEST-EFFORT:
// si falla, NO rompe el write viejo (ya commiteado), pero loguea visible y devuelve el error para el
// reporte (10.177/10.170 — nunca silencioso). `origen` = { origen_tipo, origen_id } identifica la
// fuente del/los evento(s): un movimiento bancario (banco/vinculación, externas) o una operación de
// factoring. Pasar `eventos = []` hace solo el borrado (revertir: anular factoring, desvincular).
export const escribirEventosSaldo = async (supabaseClient, origen, eventos = []) => {
  try {
    const { error: errDel } = await supabaseClient
      .from("aplicaciones_saldo")
      .delete()
      .eq("origen_tipo", origen.origen_tipo)
      .eq("origen_id", origen.origen_id);
    if (errDel) throw errDel;
    if (eventos.length > 0) {
      const { error: errIns } = await supabaseClient.from("aplicaciones_saldo").insert(eventos);
      if (errIns) throw errIns;
    }
    return { ok: true, error: null };
  } catch (e) {
    const msg = e?.message || String(e);
    console.error("[dual-write aplicaciones_saldo] FALLÓ (best-effort; la vinculación vieja NO se rompe):", msg);
    return { ok: false, error: msg };
  }
};

// Ejecuta la vinculación completa. Orden: (1) mov bancario → procesado + FK/ cuota escalar limpias;
// (2) por factura: MBF (monto aplicado) + cuotas tildadas a pagado/cobrado con movimiento_bancario_id
// + factura 'vinculada'; (3) caja consolidada (idempotente por movimiento) + FK caja en el banco.
// Idempotente por par (mov, factura) en MBF y por movimiento en caja. No traga errores (10.177).
//
// params:
//   movimiento           — fila de movimientos_bancarios (id, monto, moneda, fecha).
//   facturasSeleccionadas— [{ id, monto_total, monto_neto, monto_iva, folio, moneda,
//                             cliente_id|proveedor_id, razon_social_receptor|emisor,
//                             venta_tipo?, venta_ref_id?, venta_ref_index?,
//                             cuotasSeleccionadas?: [{id, monto_cuota}], montoAsignado? }]
//   esCobro              — true = cobro (emitidas), false = pago (recibidas).
//   pais, usuarioId
//   entidadId            — override de entidad (cliente/proveedor) resuelta en UI; default = la de f[0].
//   descripcionExtra     — sufijo para la glosa de la caja (propina/forzado breadcrumbs).
//   categoriaPagoOverride— override de categoria_pago de la caja (default cobro_factura/pago_proveedor).
export const ejecutarVinculacionFacturasCuotas = async ({
  movimiento,
  facturasSeleccionadas = [],
  esCobro,
  pais,
  usuarioId,
  entidadId = null,
  descripcionExtra = "",
  categoriaPagoOverride = null,
  supabaseClient = client,
}) => {
  if (!movimiento?.id) throw new Error("ejecutarVinculacionFacturasCuotas: movimiento sin id");
  if (facturasSeleccionadas.length === 0) throw new Error("ejecutarVinculacionFacturasCuotas: sin facturas");

  const tablaFactura = esCobro ? "facturas_sii_emitidas" : "facturas_sii_recibidas";
  const factuTipoMBF = esCobro ? "sii_emitida" : "sii_recibida";
  const campoEntidad = esCobro ? "cliente_id" : "proveedor_id";
  const esSingle = facturasSeleccionadas.length === 1;
  const ahora = new Date().toISOString();
  const entidadResuelta = entidadId != null ? entidadId : facturasSeleccionadas[0][campoEntidad];
  const entidadNombre = esCobro
    ? facturasSeleccionadas[0].razon_social_receptor
    : facturasSeleccionadas[0].razon_social_emisor;

  let cuotasVinculadas = 0;

  // 1. movimientos_bancarios → procesado. match_* limpios. B3 Fase B: ya NO se escribe la FK directa
  //    de factura — la vinculación vive en el puente MBF (insertado en el paso 2) y nadie lee la FK
  //    tras Fase A. R6: las FK escalares `plan_cobro_cuota_id`/`plan_pago_cuota_id` fueron DROPEADAS
  //    (0 lectores; el vínculo cuota↔pago vive en el libro `aplicaciones_saldo.cuota_id` + el puente)
  //    → ya no se setean.
  const updateMov = {
    estado: "procesado",
    procesado_por: usuarioId || null,
    procesado_at: ahora,
    match_entidad_tipo: null,
    match_entidad_id: null,
    match_entidad_nombre: null,
    match_factura_tipo: null,
    match_factura_id: null,
    match_confianza: null,
    match_razon: null,
  };
  {
    const { error } = await supabaseClient.from("movimientos_bancarios").update(updateMov).eq("id", movimiento.id);
    if (error) throw error;
  }

  // 2. Por factura: MBF (monto aplicado) + cuotas tildadas + marca 'vinculada'.
  for (const f of facturasSeleccionadas) {
    const cuotas = cuotasDe(f);
    const montoAplicado = montoAplicadoFactura(f);

    // 2a. MBF idempotente por (mov, tipo, factura).
    const { data: mbfExistente, error: errSel } = await supabaseClient
      .from("movimiento_bancario_facturas")
      .select("id")
      .eq("movimiento_bancario_id", movimiento.id)
      .eq("factura_tipo", factuTipoMBF)
      .eq("factura_id", f.id)
      .maybeSingle();
    if (errSel) throw errSel;
    if (!mbfExistente) {
      const { error } = await supabaseClient.from("movimiento_bancario_facturas").insert({
        movimiento_bancario_id: movimiento.id,
        factura_tipo: factuTipoMBF,
        factura_id: f.id,
        monto: montoAplicado,
      });
      if (error) throw error;
    }

    // 2b. Cuotas tildadas → pagado/cobrado + vínculo autoritativo (movimiento_bancario_id) + fecha real.
    const tablaPlan = esCobro ? "plan_cobros_facturas" : "plan_pagos_facturas";
    const cambiosCuota = esCobro
      ? { estado: "cobrado", fecha_cobro_real: movimiento.fecha, movimiento_bancario_id: movimiento.id }
      : { estado: "pagado", fecha_pago_real: movimiento.fecha, movimiento_bancario_id: movimiento.id };
    for (const c of cuotas) {
      const { error } = await supabaseClient.from(tablaPlan).update(cambiosCuota).eq("id", c.id);
      if (error) throw error;
      cuotasVinculadas += 1;
    }

    // 2c. Marcar factura 'vinculada'. plazopago/facturafechacobro: emitida siempre; recibida solo si
    //     NO tiene cuotas (con cuotas, las fechas viven en el plan) — replica el single histórico.
    const updateFactura = {
      estado_vinculacion: "vinculada",
      vinculado_por: usuarioId || null,
      vinculado_at: ahora,
    };
    if (esCobro) updateFactura.facturafechacobro = movimiento.fecha;
    else if (cuotas.length === 0) updateFactura.plazopago = movimiento.fecha;
    {
      const { error } = await supabaseClient.from(tablaFactura).update(updateFactura).eq("id", f.id);
      if (error) throw error;
    }

    // 2d. Replicar a facturas_sii_vinculaciones (solo emitidas con venta legacy poblada).
    if (esCobro && f.venta_tipo && f.venta_ref_id != null) {
      const { error } = await supabaseClient.from("facturas_sii_vinculaciones").upsert(
        {
          factura_sii_emitida_id: f.id,
          venta_tipo: f.venta_tipo,
          venta_ref_id: f.venta_ref_id,
          venta_ref_index: f.venta_ref_index ?? null,
          vinculado_por: usuarioId || null,
          vinculado_at: ahora,
        },
        { onConflict: "factura_sii_emitida_id,venta_tipo,venta_ref_id" }
      );
      if (error) throw error;
    }
  }

  // 3. Caja consolidada (una fila por movimiento, idempotente por movimiento_bancario_id).
  const { data: cajaExistente, error: errCaja } = await supabaseClient
    .from("movimientos_caja")
    .select("id")
    .eq("movimiento_bancario_id", movimiento.id)
    .maybeSingle();
  if (errCaja) throw errCaja;

  const categoria = categoriaPagoOverride || (esCobro ? "cobro_factura" : "pago_proveedor");
  let movCajaId = cajaExistente?.id || null;

  if (!cajaExistente) {
    const { neto, impuesto } = netoImpuestoConsolidado(facturasSeleccionadas);
    const folios = facturasSeleccionadas.map((f) => f.folio).join(", ");
    const { data: cajaData, error: errIns } = await supabaseClient
      .from("movimientos_caja")
      .insert([
        {
          pais,
          tipo: esCobro ? "cobro" : "pago",
          fecha: movimiento.fecha,
          monto: Math.abs(Number(movimiento.monto || 0)),
          monto_neto: neto,
          monto_impuesto: impuesto,
          moneda: movimiento.moneda || monedaLocalDe(pais),
          estado: "confirmado",
          descripcion: `${entidadNombre} | Folios ${folios}${descripcionExtra}`,
          entidad_tipo: esCobro ? "cliente" : "proveedor",
          entidad_id: entidadResuelta,
          entidad_nombre: entidadNombre,
          categoria_pago: categoria,
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        },
      ])
      .select();
    if (errIns) throw errIns;
    movCajaId = cajaData?.[0]?.id || null;
    if (movCajaId) {
      const { error } = await supabaseClient
        .from("movimientos_bancarios")
        .update({ movimiento_caja_id: movCajaId })
        .eq("id", movimiento.id);
      if (error) throw error;
    }
  } else if (!esCobro) {
    // Formalización de un pago a cuenta preexistente: la caja ya existía (bucket 'pago_factura'/
    // 'pago_credito' → badge A). Reclasificar a 'pago_proveedor' para que el badge pase de A a C.
    const { error } = await supabaseClient
      .from("movimientos_caja")
      .update({ categoria_pago: categoria })
      .eq("id", cajaExistente.id);
    if (error) throw error;
  }

  // 4. FASE 4 — dual-write espejo al libro `aplicaciones_saldo`. Best-effort, NUNCA silencioso: corre
  //    DESPUÉS de que todo el write viejo ya commiteó, así un fallo del libro no rompe el flujo del
  //    usuario. Idempotente por movimiento (borra+reinserta sus eventos).
  const { eventos: eventosSaldo, warnings: warningsSaldo } = construirEventosSaldo({
    movimiento,
    facturasSeleccionadas,
    esCobro,
    pais,
    usuarioId,
  });
  warningsSaldo.forEach((w) => console.warn("[dual-write aplicaciones_saldo]", w));
  const libroResult = await escribirEventosSaldo(
    supabaseClient,
    { origen_tipo: "movimiento_bancario", origen_id: movimiento.id },
    eventosSaldo
  );

  return {
    movCajaId,
    facturasVinculadas: facturasSeleccionadas.length,
    cuotasVinculadas,
    fkEscalar: esSingle,
    libro: { ok: libroResult.ok, error: libroResult.error, eventos: eventosSaldo.length, warnings: warningsSaldo },
  };
};
